import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import AppImage from '@/components/AppImage';

export default function CatererLanding() {
  const navigate = useNavigate();
  const [showAppComingSoonModal, setShowAppComingSoonModal] = useState(false);

  return (
    <div className="min-h-screen bg-[#fafafa] text-foreground">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <AppImage src="/logo.png" alt="Mrs Cater" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/onboarding')}>
              Activate profile
            </Button>
            <Button onClick={() => navigate('/login')}>Login</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <img
          src="/assets/images/catering.png"
          alt="Professional catering service"
          className="absolute inset-0 w-full h-full object-cover opacity-50 scale-105"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 py-20 md:py-28 text-white">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
            Grow your catering business with Mrs Cater
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-2xl">
            Get qualified event requests, send bids, receive secure payments, and manage orders from one dashboard.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button className="text-base" onClick={() => navigate('/onboarding')}>
              Activate my caterer profile
            </Button>
            <Button variant="outline" className="text-base bg-white/10 border-white/40 text-white hover:bg-white/20" onClick={() => navigate('/login')}>
              I already have an account
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-5 text-sm text-white/90">
            <span className="flex items-center gap-2"><Icon name="ShieldCheck" size={16} /> Secure payouts</span>
            <span className="flex items-center gap-2"><Icon name="Gavel" size={16} /> Fast bid management</span>
            <span className="flex items-center gap-2"><Icon name="MessageCircle" size={16} /> Customer messaging</span>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-14 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: 'ClipboardList',
            title: 'Receive event requests',
            text: 'Browse special requests and paid orders from customers planning events near you.',
          },
          {
            icon: 'Wallet',
            title: 'Get paid securely',
            text: 'Track earnings and payouts with clear status updates from order to bank transfer.',
          },
          {
            icon: 'LayoutDashboard',
            title: 'Run operations faster',
            text: 'Manage menu items, orders, messages, reviews, and business profile in one place.',
          },
        ].map((item) => (
          <div key={item.title} className="rounded-xl border border-border bg-white p-6">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Icon name={item.icon} size={18} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
            <p className="text-sm text-muted-foreground">{item.text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 lg:px-8 pb-16">
        <div className="rounded-2xl bg-white border border-border p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold">Ready to start receiving orders?</h2>
            <p className="text-muted-foreground mt-2">Complete your caterer profile and start bidding for event opportunities today.</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/onboarding')}>Complete profile</Button>
            <Button variant="outline" onClick={() => navigate('/login')}>Login</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AppImage src="/logo.png" alt="Mrs Cater" className="h-10 w-auto brightness-0 invert" />
            </div>
            <p className="text-sm text-background/70 leading-relaxed">
              The professional catering platform for South African caterers. Manage orders, grow your business.
            </p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowAppComingSoonModal(true)}
                className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg border border-background/20 hover:bg-background/10 transition"
              >
                <img src="/assets/images/app-store.svg" alt="App Store" className="h-7 w-auto" />
              </button>
              <button
                onClick={() => setShowAppComingSoonModal(true)}
                className="flex-1 flex items-center justify-center px-3 py-2 rounded-lg border border-background/20 hover:bg-background/10 transition"
              >
                <img src="/assets/images/google-play.svg" alt="Google Play" className="h-7 w-auto" />
              </button>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h4 className="font-semibold text-background mb-4 text-sm uppercase tracking-wide">Platform</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li>
                <a href="https://www.mrscater.com" target="_blank" rel="noopener noreferrer" className="hover:text-background transition">
                  For Customers
                </a>
              </li>
              <li><Link to="/contact-us" className="hover:text-background transition">Contact Us</Link></li>
              <li><Link to="/submit-idea" className="hover:text-background transition">Submit Idea</Link></li>
              <li><Link to="/report-problem" className="hover:text-background transition">Report Problem</Link></li>
              <li><Link to="/delete-account" className="hover:text-background transition">Delete Account</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-background mb-4 text-sm uppercase tracking-wide">Legal</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/terms-of-service" className="hover:text-background transition">Terms of Service</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-background transition">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-background transition">Refund Policy</Link></li>
              <li><Link to="/acceptable-use-policy" className="hover:text-background transition">Acceptable Use</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-background transition">Shipping Policy</Link></li>
              <li><Link to="/returns-policy" className="hover:text-background transition">Returns Policy</Link></li>
              <li><Link to="/compliance" className="hover:text-background transition">Compliance</Link></li>
            </ul>
          </div>

          {/* Trust & Safety */}
          <div>
            <h4 className="font-semibold text-background mb-4 text-sm uppercase tracking-wide">Trust &amp; Safety</h4>
            <ul className="space-y-2 text-sm text-background/70">
              <li><Link to="/caterer-verification" className="hover:text-background transition">Caterer Verification</Link></li>
              <li><Link to="/trust-score" className="hover:text-background transition">Trust Score</Link></li>
              <li><Link to="/payments" className="hover:text-background transition">Payments &amp; Escrow</Link></li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-background/10 text-center text-xs text-background/50">
          <span>© {new Date().getFullYear()} Mrs Cater. Built with dignity.</span>
        </div>
      </footer>

      {/* App Coming Soon Modal */}
      {showAppComingSoonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowAppComingSoonModal(false)}>
          <div className="bg-background rounded-2xl p-8 max-w-sm w-full text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Smartphone" size={28} className="text-primary" />
            </div>
            <h3 className="text-xl font-bold mb-2">App Coming Soon</h3>
            <p className="text-muted-foreground text-sm mb-6">
              The Mrs Cater caterer app is in development. Use the web app in your browser for now, it works great on mobile too!
            </p>
            <Button className="w-full" onClick={() => setShowAppComingSoonModal(false)}>Got it</Button>
          </div>
        </div>
      )}
    </div>
  );
}
